import { fxHint } from "../lib/fx";
import React, { useState, useEffect, useRef } from "react";
import { ShoppingCart, Trash2, Minus, Plus, X, Loader2, Wifi, WifiOff } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { fmt } from "../lib/format";
import { say } from "../lib/voice";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent } from "./ui/sheet";
import { customerQueue, isNetworkError } from "../lib/offlineQueue";

export default function BetCartBar() {
  const { items, total, count, removeItem, updateQty, clear, removeByGame } = useCart();
  const [closedIds, setClosedIds] = useState([]);
  const itemsRef = useRef(items); itemsRef.current = items;
  const { user, wallet, refreshWallet } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawSlots, setDrawSlots] = useState([]);
  useEffect(() => {
    // when confirming, fetch each show's live runtime so the sheet names the exact draw (interval shows: "5:30 PM")
    if (!confirm || items.length === 0) { setDrawSlots([]); return; }
    Promise.all([...new Set(items.map((i) => i.game_id))].map((id) => api.get(`/games/${id}`).then((r) => r.data).catch(() => null))).then((gs) => {
      const to12 = (tm) => { if (!tm) return ""; const [h, m] = tm.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
      setDrawSlots(gs.filter(Boolean).map((g) => `${g.name} ${to12(g.runtime?.mode === "interval" ? g.runtime?.draw_slot : g.draw_time)}`));
    });
  }, [confirm, items]);
  useEffect(() => {
    // Auto-clear: while the cart has items, watch each show's runtime; the moment a show closes its numbers are removed with a notice.
    if (items.length === 0) { setClosedIds([]); return; }
    let alive = true, timer = null;
    const check = async () => {
      const ids = [...new Set(itemsRef.current.map((i) => i.game_id))];
      const gs = await Promise.all(ids.map((id) => api.get(`/games/${id}`).then((r) => r.data).catch(() => null)));
      if (!alive) return;
      const closed = gs.filter((g) => g && g.runtime && g.runtime.is_open === false && g.runtime.mode !== "interval");
      if (closed.length) {
        const n = itemsRef.current.filter((i) => closed.some((g) => g.id === i.game_id)).length;
        closed.forEach((g) => removeByGame(g.id));
        toast.warning(`⏰ ${closed.map((g) => g.name).join(", ")} closed — ${n} number${n > 1 ? "s" : ""} removed from cart`, { description: "Show close ஆகிடுச்சு · cart-ல இருந்து தானா நீக்கப்பட்டது", duration: 8000 });
        setClosedIds([]);
        return;
      }
      const nextSecs = Math.min(30, ...gs.filter((g) => g && g.runtime && g.runtime.mode !== "interval" && g.runtime.closing_seconds > 0).map((g) => g.runtime.closing_seconds));
      timer = setTimeout(check, Math.max(2, nextSecs) * 1000 + 500);
    };
    check();
    return () => { alive = false; clearTimeout(timer); };
  }, [items.map((i) => i.game_id).join(",")]); // eslint-disable-line
  const closedItems = items.filter((i) => closedIds.includes(i.game_id));
  const clearClosed = () => { closedIds.forEach((id) => removeByGame(id)); setClosedIds([]); };
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  if (!items.length) return null;

  const placeBet = async () => {
    if (!user) {
      toast.error("Please login to place a bet");
      navigate("/auth");
      return;
    }
    const payload = {
      idempotency_key: `${user.id}-${Date.now()}`,
      items: items.map((i) => ({ game_id: i.game_id, slat_id: i.slat_id, number: i.number, quantity: i.quantity, rate_type: i.rate_type || "customer", mode: i.mode || "direct" })),
    };
    const saveOffline = () => {
      const r = customerQueue.add({ ...payload, total, game_name: items[0]?.game_name });
      if (!r.ok) { toast.error("Duplicate — these numbers are already PENDING in your offline queue"); return; }
      toast.message("Saved OFFLINE (PENDING — not booked yet). Confirmed when internet returns", { description: "Turn internet ON before the show closes. Amount is deducted only on confirmation.", duration: 8000 });
      clear(); setConfirm(false); setOpen(false);
    };
    if (!navigator.onLine) return saveOffline();
    setLoading(true);
    try {
      const { data } = await api.post("/bets/place", payload);
      const draws = (data.draws || []).map((d) => { const [g, tm] = d.split("|"); const [h, m] = (tm || "").split(":").map(Number); return `${g} ${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; });
      toast.success(`Bet placed! ${fmt(data.total)} on ${data.num_bids} number(s)${draws.length ? ` · for ${draws.join(", ")} draw` : ""}`, { description: `${draws.length ? `🎯 ${draws.join(", ")} draw-க்கு · ` : ""}${data.balance != null ? `${fmt(data.total)} கழிந்தது · மீதி Balance ${fmt(data.balance)}` : ""}`, duration: 8000 });
      if (data.streak) setTimeout(() => toast.success(`🔥 ${data.streak.run}-slot streak! ₹${data.streak.bonus} free ticket added to your wallet`, { duration: 8000 }), 600);
      say(`Book ஆயிடுச்சு. ${data.num_bids} ticket, ${Math.round(data.total)} ரூபாய். All the best!`);
      clear();
      setConfirm(false);
      setOpen(false);
      refreshWallet(); window.dispatchEvent(new Event("rl-bets-placed"));
      navigate("/my-bets");
    } catch (e) {
      if (isNetworkError(e)) saveOffline();
      else if (e.response?.status === 402) toast.error(formatApiError(e.response?.data?.detail), { duration: 8000, action: { label: "Recharge", onClick: () => navigate("/recharge") } });
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-[64px] left-1/2 -translate-x-1/2 w-full max-w-[480px] px-3 z-30" data-testid="bet-cart-bar">
        <button
          onClick={() => setOpen(true)}
          className="w-full rl-gold-bg rounded-2xl px-4 py-3 flex items-center justify-between shadow-lg shadow-amber-900/30 no-tap active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-2">
            <div className="relative">
              <ShoppingCart className="w-5 h-5" />
              <span className="absolute -top-2 -right-2 bg-[#0B132B] text-blue-600 text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{count}</span>
            </div>
            <span className="font-bold text-sm">{items.length} Bids · View Cart</span>
          </div>
          <span className="font-mono font-black">{fmt(total)}</span>
        </button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rl-glass border-slate-200 rounded-t-3xl max-w-[480px] mx-auto max-h-[80vh] overflow-y-auto" data-testid="cart-sheet">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-lg text-slate-900">Bet Cart</h3>
            <button onClick={clear} className="text-xs text-red-400 flex items-center gap-1" data-testid="cart-clear-btn"><Trash2 className="w-3.5 h-3.5" />Clear</button>
          </div>
          {closedItems.length > 0 && (
            <div className="mb-3 rounded-xl bg-red-50 border border-red-200 p-3 flex items-center justify-between gap-2" data-testid="cart-closed-banner">
              <div className="text-xs text-red-800"><b>{closedItems.length}</b> number(s) belong to a show that has <b>closed</b> — they can't be booked now.</div>
              <button onClick={clearClosed} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold shrink-0" data-testid="cart-clear-closed-btn">Clear closed</button>
            </div>
          )}
          <div className="space-y-2">
            {items.map((i) => (
              <div key={i.key} className="rl-card p-3 flex items-center justify-between" data-testid={`cart-item-${i.key}`}>
                <div>
                  <div className="text-xs text-slate-500">{i.game_name} · {i.slat_name}{closedIds.includes(i.game_id) && <span className="ml-1 text-[9px] font-black text-red-600 bg-red-100 px-1 rounded">CLOSED</span>}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="rl-ball w-8 h-8 text-sm">{i.number}</span>
                    {i.mode === "box" && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`cart-box-tag-${i.key}`}>BOX ×{i.perms}</span>}
                    <span className="text-xs text-slate-500">@ {fmt(i.rate)}{i.mode === "box" ? ` × ${i.perms}` : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 rl-card px-1 py-0.5">
                    <button onClick={() => updateQty(i.key, i.quantity - 1)} className="w-6 h-6 flex items-center justify-center"><Minus className="w-3 h-3 text-blue-600" /></button>
                    <span className="w-5 text-center text-sm font-bold">{i.quantity}</span>
                    <button onClick={() => updateQty(i.key, i.quantity + 1)} className="w-6 h-6 flex items-center justify-center"><Plus className="w-3 h-3 text-blue-600" /></button>
                  </div>
                  <button onClick={() => removeItem(i.key)}><X className="w-4 h-4 text-slate-500" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 mb-2 px-1">
            <span className="text-slate-500">Total ({count} tickets)</span>
            <span className="font-mono font-black text-blue-600 text-lg">{fmt(total)}{fxHint(total) && <span className="ml-1.5 text-[11px] font-semibold text-slate-400" data-testid="fx-hint">{fxHint(total)}</span>}</span>
          </div>
          {wallet && (() => { const bal = +wallet.total || 0, short = Math.max(0, total - bal); return (
            <div className={`rounded-xl px-3 py-2 mb-2 text-[11px] leading-snug border flex items-center justify-between gap-2 ${short > 0 ? "bg-red-50 border-red-200 text-red-800" : "bg-slate-50 border-slate-200 text-slate-600"}`} data-testid="cart-balance-split">
              <span>Balance <b>{fmt(bal)}</b>{short === 0 && <> → after booking <b>{fmt(bal - total)}</b></>}</span>
              {short > 0 && <button onClick={() => navigate(`/recharge?amount=${Math.ceil(short / 100) * 100}`)} className="font-bold underline whitespace-nowrap" data-testid="cart-balance-split-detail">Short {fmt(short)} · Recharge</button>}
            </div>); })()}
          <button onClick={() => setConfirm(true)} className="w-full rl-gold-bg font-bold py-3.5 rounded-2xl no-tap" data-testid="cart-paynow-btn">
            Pay Now · {fmt(total)}
          </button>
        </SheetContent>
      </Sheet>

      <Sheet open={confirm} onOpenChange={setConfirm}>
        <SheetContent side="bottom" className="rl-glass border-slate-200 rounded-t-3xl max-w-[480px] mx-auto" data-testid="confirm-sheet">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Confirm Your Bet</h3>
          <div className="rl-card p-4 space-y-2 max-h-[40vh] overflow-y-auto">
            {items.map((i) => (
              <div key={i.key} className="flex items-center justify-between text-sm border-b border-slate-200 pb-2">
                <span className="text-slate-600">{i.game_name} · {i.slat_name} · <b className="text-blue-600">{i.number}</b>{i.mode === "box" && <b className="text-amber-600"> BOX</b>}</span>
                <span className="text-slate-500">{i.quantity}{i.mode === "box" ? ` × ${i.perms}` : ""} × {fmt(i.rate)}</span>
              </div>
            ))}
          </div>
          {drawSlots.length > 0 && <div className="mt-2 rounded-xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900 font-bold" data-testid="confirm-draw-slot">🎯 Draw: {drawSlots.join(" · ")} · இந்த ticket இந்த draw-க்கு</div>}
          <div className="flex items-center justify-between my-3 px-1">
            <span className="text-slate-500">Amount to be deducted</span>
            <span className="font-mono font-black text-blue-600">{fmt(total)}</span>
          </div>
          <div className={`rounded-xl px-3 py-2 mb-3 text-[11px] flex items-start gap-2 ${online ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-slate-900 text-white"}`} data-testid="net-rule-note">
            {online ? <Wifi className="w-3.5 h-3.5 shrink-0 mt-0.5" /> : <WifiOff className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" />}
            <span>{online ? <><b>Internet ON கட்டாயம்</b> — booking confirm ஆக net இருக்க வேண்டும். Booking is confirmed only while online.</> : <><b>You are OFFLINE.</b> This booking will be saved on your phone and confirmed only when internet returns — before the show closes. Amount is deducted only on confirmation.</>}</span>
          </div>
          <button onClick={placeBet} disabled={loading} className="w-full rl-gold-bg font-bold py-3.5 rounded-2xl no-tap flex items-center justify-center gap-2" data-testid="confirm-bet-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {online ? "Confirm & Pay" : "Save offline (confirm when online)"}
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
}
